// src/Pages/Admin/WorkersPerformance.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { useWorkerPerformance } from "../../hooks/useWorkerPerformance";

// ===== Helpers fechas (default: mes actual) =====
const todayStr = () => new Date().toISOString().slice(0, 10);

const startOfMonthStr = () => {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

function WorkersPerformance() {
  const [workers, setWorkers] = useState([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState("");
  // ✅ Default: mes actual → hoy (pero editable)
  const [fromDate, setFromDate] = useState(startOfMonthStr());
  const [toDate, setToDate] = useState(todayStr());
  const [loadingWorkers, setLoadingWorkers] = useState(true);

  useEffect(() => {
    const loadWorkers = async () => {
      try {
        const profesionalesRef = collection(db, "profesionales");
        const snap = await getDocs(profesionalesRef);

        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const activos = list.filter(
          (p) => p.activo === true || p.activo === undefined
        );

        setWorkers(activos);
        if (activos.length > 0) setSelectedWorkerId(activos[0].id);
      } catch (e) {
        console.error("Error cargando profesionales:", e);
      } finally {
        setLoadingWorkers(false);
      }
    };

    loadWorkers();
  }, []);

  const { loading, stats, error } = useWorkerPerformance(selectedWorkerId, {
    fromDate,
    toDate,
  });

  const worker = useMemo(
    () => workers.find((w) => w.id === selectedWorkerId),
    [workers, selectedWorkerId]
  );

  const workerName = worker?.nombre || "Profesional";

  return (
    <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Desempeño de trabajadores
          </h1>
          <p className="text-sm text-slate-600">
            Selecciona un profesional para revisar sus sesiones y pacientes en el
            período.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500 mb-1">
              Desde
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-slate-500 mb-1">
              Hasta
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <aside className="lg:w-64">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-semibold text-slate-900 mb-3">
              Trabajadores
            </h2>

            {loadingWorkers ? (
              <p className="text-xs text-slate-500">Cargando…</p>
            ) : workers.length === 0 ? (
              <p className="text-xs text-slate-500">
                No hay trabajadores configurados.
              </p>
            ) : (
              <div className="space-y-1">
                {workers.map((w) => {
                  const isActive = w.id === selectedWorkerId;
                  return (
                    <button
                      key={w.id}
                      onClick={() => setSelectedWorkerId(w.id)}
                      className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${
                        isActive
                          ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
                          : "hover:bg-slate-50 text-slate-700 border border-transparent"
                      }`}
                    >
                      <p className="font-medium truncate">
                        {w.nombre || "Sin nombre"}
                      </p>
                      <p className="text-xs text-slate-500 truncate">{w.correo}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        <main className="flex-1">
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Ocurrió un error al cargar los datos.
            </div>
          )}

          {loadingWorkers ? (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
              Cargando información…
            </div>
          ) : !selectedWorkerId || !worker ? (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
              Selecciona un trabajador para ver su desempeño.
            </div>
          ) : loading || !stats ? (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
              Cargando información…
            </div>
          ) : (
            <>
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-slate-900">
                  Mi desempeño clínico
                </h2>
                <p className="text-sm text-slate-500">
                  Resumen de las sesiones y pacientes de{" "}
                  <span className="font-medium">{workerName}</span> en el período
                  seleccionado.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 mb-6">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-xs text-slate-500 mb-1">
                    Sesiones en el período
                  </p>
                  <p className="text-2xl font-semibold text-slate-900">
                    {stats.totalSesiones}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {stats.completadas} realizadas · {stats.canceladas} canceladas ·{" "}
                    {stats.pendientesAprobadas} pendientes/aprobadas
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-xs text-slate-500 mb-1">Tasa de asistencia</p>
                  <p className="text-2xl font-semibold text-emerald-600">
                    {stats.tasaAsistencia}%
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Realizadas vs canceladas
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-xs text-slate-500 mb-1">Pacientes nuevos</p>
                  <p className="text-2xl font-semibold text-slate-900">
                    {stats.pacientesNuevos}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Primera sesión en el período
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-xs text-slate-500 mb-1">
                    Pacientes recurrentes
                  </p>
                  <p className="text-2xl font-semibold text-slate-900">
                    {stats.pacientesRecurrentes}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Ya estaban antes del período
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      Servicios más frecuentes
                    </p>
                    <p className="text-xs text-slate-500">
                      Valor por sesión y cuántas sesiones se realizaron en el período
                    </p>
                  </div>

                  <div className="text-xs text-slate-400">
                    {(stats.totalRealizadas ?? stats.completadas ?? 0)} sesión(es) realizadas
                  </div>
                </div>

                <div className="space-y-3">
                  {stats.serviciosFrecuentes.map((s) => {
                    const denom = stats.totalRealizadas ?? stats.completadas ?? 0;
                    const realizadas = s.realizadas ?? 0;
                    const pct = denom === 0 ? 0 : (realizadas / denom) * 100;

                    const valorSesionTxt =
                      typeof s.valorSesion === "number"
                        ? `$${s.valorSesion.toLocaleString("es-CL")}`
                        : "—";

                    return (
                      <div key={s.nombre}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm">
                          <span className="text-slate-700">{s.nombre}</span>

                          <span className="text-slate-500">
                            Valor/sesión:{" "}
                            <strong className="text-slate-700">{valorSesionTxt}</strong>
                            {" · "}
                            <strong className="text-slate-700">{realizadas}</strong>{" "}
                            realizadas
                            <span className="text-slate-400">
                              {" "}
                              (total {s.sesiones ?? 0})
                            </span>
                          </span>
                        </div>

                        <div className="mt-1 h-2 w-full rounded-full bg-slate-100">
                          <div
                            className="h-2 rounded-full bg-indigo-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}

                  {stats.serviciosFrecuentes.length === 0 && (
                    <p className="text-xs text-slate-400">
                      No hay servicios registrados en este período.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default WorkersPerformance;